import { useState } from 'react'

import { type Note, firstLineOf } from '../../api/notes'
import { Button } from '../../ui/Control'
import { Icon } from '../../ui/Icon'
import { Preview } from '../../ui/preview/Preview'

/**
 * One note on the pad, closed.
 *
 * It shows its **first line, drawn as it was written** — `## Fristen` is a heading, `**wichtig**` is
 * bold — because that is the same rule the library follows with a document: show the content, do not
 * name it. A note listed as `Note 3 · edited 11:04` would be the file list this whole product is meant
 * not to feel like.
 *
 * Everything that can be done to it is a **visible control**: moving it up, moving it down, striking it
 * through. Dragging is faster and is offered as well, and it is never the only way — a gesture nobody
 * can see is a gesture nobody can be told about.
 */
export interface NoteCardProps {
  readonly note: Note
  readonly open: boolean
  readonly onOpen: () => void
  /** `null` where there is nothing above or below it, which is what greys the control out. */
  readonly onUp: (() => void) | null
  readonly onDown: (() => void) | null
  readonly onStrike: () => void
  readonly onBringBack: () => void
  /** Removes it now, rather than waiting out the four days. Only offered on a struck note. */
  readonly onRemove: () => void
  /** Says whether a note is being dragged right now, so the gaps between cards can offer themselves. */
  readonly onDragStart: () => void
  readonly onDragEnd: () => void
}

/** What a dragged note puts on the clipboard, so a drop can tell it from anything else. */
export const DRAGGED_NOTE = 'application/x-mylib-note'

export function NoteCard({
  note,
  open,
  onOpen,
  onUp,
  onDown,
  onStrike,
  onBringBack,
  onRemove,
  onDragStart,
  onDragEnd,
}: NoteCardProps) {
  const line = firstLineOf(note)
  const struck = note.struckAt !== null

  /**
   * What a dragged note puts on the clipboard.
   *
   * Named, and named first: a drag that carries no data of its own is filled in by the browser from
   * whatever it finds inside the element, and then a drop cannot tell it from anything else.
   */
  const carry = (data: DataTransfer) => {
    data.setData(DRAGGED_NOTE, note.id)
    data.setData('text/plain', line)
    data.effectAllowed = 'move'
    onDragStart()
  }

  return (
    <li
      className={struck ? 'note note--struck' : 'note'}
      draggable={!struck}
      onDragStart={(event) => {
        carry(event.dataTransfer)
      }}
      onDragEnd={onDragEnd}
    >
      {/*
        The whole card opens the note, and the control that does it lies *behind* the drawn line rather
        than around it. Around it is not possible: the line is drawn markup — a heading, a list, a link
        — and none of that may stand inside a button. So the button fills the card, the drawn line sits
        on top of it and lets clicks through, and the note is still one tap away.
      */}
      <button
        type="button"
        className="note__hit"
        aria-current={open ? 'true' : undefined}
        onClick={onOpen}
        /*
          Draggable in its own right, and not only through the list item around it. This button
          covers the whole card — it has to, because the drawn line above it cannot be inside a
          button — so it is what the pointer meets almost everywhere, and whether a drag started on
          a form control carries up to its ancestor is exactly the kind of thing browsers disagree
          about. Saying it here costs one attribute and settles it.
        */
        draggable={!struck}
        onDragStart={(event) => {
          carry(event.dataTransfer)
        }}
        onDragEnd={onDragEnd}
      >
        {line === '' ? 'Empty note — tap to write' : line}
      </button>
      {/*
        The one region that unmistakably drags, and the reason it exists.

        The card drags too — from the list item and from the button covering it — but *what the
        pointer meets* differs across the card: the controls on the right are their own buttons and
        never were a drag source, and whether a drag started on a form control carries up to its
        ancestor is something browsers disagree about. A grip answers the question "where do I take
        hold of this" by being visible, and it is the drag source in its own right.

        Dragging is a shortcut all the same. The two controls beside it do the same job with a
        finger, on a phone, and by keyboard — where a drag does not work at all.
      */}
      {!struck && (
        <span
          className="note__grip"
          aria-hidden="true"
          draggable
          onDragStart={(event) => {
            // The card as the picture that follows the pointer, rather than the grip alone: what is
            // being moved is the note, and a lone handle drifting across the screen says nothing.
            const card = event.currentTarget.closest('li')
            if (card !== null) {
              event.dataTransfer.setDragImage(card, 24, card.clientHeight / 2)
            }
            carry(event.dataTransfer)
          }}
          onDragEnd={onDragEnd}
        >
          <Icon name="grip" small />
        </span>
      )}

      <p className="note__when">
        {struck ? 'Struck out · ' : ''}
        {whenWritten(note.createdAt)}
      </p>

      <div className="note__line" aria-hidden="true">
        {line === '' ? (
          <span className="note__empty">Empty note — tap to write</span>
        ) : (
          /*
            The first line only, drawn. A whole note in the list would make the pad a wall of text; the
            first line is reliably what it is about, which is why it is also what the search weighs as
            its title.
          */
          <Preview text={line} format={note.format} label="First line" />
        )}
      </div>

      <div className="note__doing">
        {struck ? (
          <>
            <Button tone="bare" onClick={onBringBack}>
              Put back
            </Button>
            {/*
              The second stage of striking one, for somebody who knows they are finished with it and
              does not want to look at it for four days. Nothing is asked, exactly as the library asks
              nothing when one entry is removed from the trash — it is the *whole* trash that asks.
            */}
            <Button tone="bare" className="control--danger" onClick={onRemove}>
              Delete now
            </Button>
          </>
        ) : (
          <>
            <Button
              tone="bare"
              icon="up"
              iconOnlyLabel="Move this note up"
              disabled={onUp === null}
              onClick={() => onUp?.()}
            />
            <Button
              tone="bare"
              icon="down"
              iconOnlyLabel="Move this note down"
              disabled={onDown === null}
              onClick={() => onDown?.()}
            />
            {/*
              Nothing is asked. It is drawn through, goes to the end, and "Put back" stands right there
              — so striking one is a gesture rather than a decision.

              A picture like the two beside it, because as a word it took a third of a narrow card and
              pushed the note's own line into three.
            */}
            <Button
              tone="bare"
              className="control--danger"
              icon="strike"
              iconOnlyLabel="Strike this note out"
              onClick={onStrike}
            />
          </>
        )}
      </div>
    </li>
  )
}

/**
 * When a note was written, in the shape that says the most about it.
 *
 * The time for something written today, the weekday for this week, a date after that. A pad holds a
 * few days' worth of thinking — that is what the retention is for — so "11:04" and "Tuesday" are what
 * place a note among the others, and a full date on everything would say least where it matters most.
 */
export function whenWritten(iso: string): string {
  const at = new Date(iso)
  if (Number.isNaN(at.getTime())) {
    return ''
  }
  const ageInDays = (Date.now() - at.getTime()) / MILLISECONDS_A_DAY
  if (at.toDateString() === new Date().toDateString()) {
    return at.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
  }
  if (ageInDays < DAYS_IN_A_WEEK) {
    return at.toLocaleDateString(undefined, { weekday: 'long' })
  }
  return at.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

const MILLISECONDS_A_DAY = 24 * 60 * 60 * 1000
const DAYS_IN_A_WEEK = 7

/**
 * The gap between two cards, as a place a dragged note can be dropped.
 *
 * A gap rather than the card itself: dropping *onto* a note would have to mean something — before it, or
 * after it? — and the answer would be a rule nobody can see. A gap is the place, and it says so by
 * opening up when something is being dragged over it.
 */
export function Gap({
  index,
  active,
  onDrop,
}: {
  readonly index: number
  /** Whether a note is being dragged at all. The gap is there either way; this is how it looks. */
  readonly active: boolean
  readonly onDrop: (note: string, index: number) => void
}) {
  const [over, setOver] = useState(false)

  /*
   * **Always rendered, and always the same height.** It used to appear only once a drag had begun,
   * which meant every gap in the pad opened up at that moment and the whole list jumped out from
   * under the pointer — a note was then being dragged towards a place that had already moved. It is
   * there the whole time now and only changes how it looks, so nothing reflows mid-drag.
   */
  return (
    <li
      className={
        over ? 'note-gap note-gap--over' : active ? 'note-gap note-gap--ready' : 'note-gap'
      }
      aria-hidden="true"
      onDragEnter={(event) => {
        // Some browsers decide here rather than on the first `dragover`, so both say the same thing.
        if (active && event.dataTransfer.types.includes(DRAGGED_NOTE)) {
          event.preventDefault()
          setOver(true)
        }
      }}
      onDragOver={(event) => {
        if (active && event.dataTransfer.types.includes(DRAGGED_NOTE)) {
          event.preventDefault()
          event.dataTransfer.dropEffect = 'move'
          setOver(true)
        }
      }}
      onDragLeave={() => {
        setOver(false)
      }}
      onDrop={(event) => {
        const moved = event.dataTransfer.getData(DRAGGED_NOTE)
        if (moved !== '') {
          event.preventDefault()
          setOver(false)
          onDrop(moved, index)
        }
      }}
    />
  )
}
